import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Plus,
  Trash2,
  TrendingUp,
  BellRing,
  CalendarOff,
  Sparkles,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  useGetLeavePolicyQuery,
  useUpdateLeavePolicyMutation,
  type EntitlementTier,
  type NoticeTier,
} from '@/redux/service/leavePolicy'

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// A card section's icon-chip header — same shape as ExpenseRatioHistory's
// and Configuration's section headers, so this panel reads as one of the
// family rather than a one-off.
const SectionHeader = ({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: React.ElementType
  title: string
  description: string
  action?: React.ReactNode
}) => (
  <div className='flex items-center justify-between px-6 py-5 border-b border-border bg-muted/80'>
    <div className='flex items-center gap-4'>
      <div className='w-10 h-10 rounded-xl bg-muted flex items-center justify-center text-muted-foreground shrink-0'>
        <Icon className='w-5 h-5' />
      </div>
      <div>
        <h3 className='text-sm font-bold text-foreground'>{title}</h3>
        <p className='text-xs text-muted-foreground mt-0.5'>{description}</p>
      </div>
    </div>
    {action}
  </div>
)

const daysPerYear = (daysPerMonth: number) =>
  Math.round(daysPerMonth * 12 * 10) / 10

const LeavePolicy = () => {
  const { data, isLoading } = useGetLeavePolicyQuery()
  const [updateLeavePolicy, { isLoading: isSaving }] =
    useUpdateLeavePolicyMutation()

  const [entitlementTiers, setEntitlementTiers] = useState<EntitlementTier[]>(
    []
  )
  const [noticeTiers, setNoticeTiers] = useState<NoticeTier[]>([])
  const [probationGateEnabled, setProbationGateEnabled] = useState(true)
  const [specialLeaveDays, setSpecialLeaveDays] = useState('1')
  const [weeklyOffDays, setWeeklyOffDays] = useState<number[]>([0, 6])
  const [sandwichRuleEnabled, setSandwichRuleEnabled] = useState(true)

  useEffect(() => {
    if (!data?.data) return
    setEntitlementTiers(data.data.entitlement_tiers)
    setNoticeTiers(data.data.notice_tiers)
    setProbationGateEnabled(data.data.probation_gate_enabled)
    setSpecialLeaveDays(String(data.data.special_leave_days))
    setWeeklyOffDays(data.data.weekly_off_days)
    setSandwichRuleEnabled(data.data.sandwich_rule_enabled)
  }, [data])

  const toggleWeeklyOffDay = (day: number) => {
    setWeeklyOffDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day].sort((a, b) => a - b)
    )
  }

  const handleSave = async () => {
    try {
      await updateLeavePolicy({
        entitlement_tiers: entitlementTiers,
        probation_gate_enabled: probationGateEnabled,
        special_leave_days: Number(specialLeaveDays),
        notice_tiers: noticeTiers,
        weekly_off_days: weeklyOffDays,
        sandwich_rule_enabled: sandwichRuleEnabled,
      }).unwrap()
      toast.success('Leave policy updated')
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ||
        'Failed to update leave policy'
      toast.error(message)
    }
  }

  if (isLoading) {
    return (
      <div className='space-y-6'>
        {[1, 2, 3].map(i => (
          <div key={i} className='rounded-2xl border border-border p-6'>
            <div className='flex items-center gap-4 mb-4'>
              <Skeleton className='w-10 h-10 rounded-xl' />
              <Skeleton className='h-4 w-48' />
            </div>
            <Skeleton className='h-24 w-full rounded-xl' />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className='h-full flex flex-col animate-in fade-in slide-in-from-bottom-2 duration-500 space-y-8 overflow-y-auto'>
      <div className='shrink-0 space-y-1'>
        <h2 className='text-xl font-semibold text-foreground'>Leave Policy</h2>
        <p className='text-sm text-muted-foreground leading-relaxed'>
          Configure how Paid Leave accrues, when Special Leave is granted,
          notice-period requirements, and the sandwich-leave rule. Changes apply
          going forward only.
        </p>
      </div>

      {/* Paid Leave Accrual */}
      <div className='rounded-2xl border border-border overflow-hidden shrink-0'>
        <SectionHeader
          icon={TrendingUp}
          title='Paid Leave Accrual'
          description='How many days credit each month, by tenure'
          action={
            <Button
              type='button'
              size='sm'
              className='h-8 gap-1.5'
              onClick={() =>
                setEntitlementTiers(prev => [
                  ...prev,
                  { min_years: 0, max_years: null, days_per_month: 1 },
                ])
              }
            >
              <Plus className='w-3.5 h-3.5' /> Add Tier
            </Button>
          }
        />
        <div className='px-6 py-6 space-y-6'>
          {entitlementTiers.length === 0 ? (
            <p className='text-sm text-muted-foreground text-center py-6'>
              No accrual tiers configured — Paid Leave won't accrue until you
              add one.
            </p>
          ) : (
            <div className='border border-border rounded-xl overflow-hidden'>
              <Table>
                <TableHeader>
                  <TableRow className='bg-muted/60'>
                    <TableHead>From (years)</TableHead>
                    <TableHead>To (years)</TableHead>
                    <TableHead>Days / month</TableHead>
                    <TableHead className='text-right'>≈ Days / year</TableHead>
                    <TableHead className='w-10' />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entitlementTiers.map((tier, i) => (
                    <TableRow key={i} className='hover:bg-muted/40'>
                      <TableCell>
                        <Input
                          type='number'
                          min={0}
                          value={tier.min_years}
                          onChange={e => {
                            const next = [...entitlementTiers]
                            next[i] = {
                              ...tier,
                              min_years: Number(e.target.value),
                            }
                            setEntitlementTiers(next)
                          }}
                          className='w-24 h-9 rounded-lg'
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type='number'
                          min={0}
                          placeholder='No limit'
                          value={tier.max_years ?? ''}
                          onChange={e => {
                            const next = [...entitlementTiers]
                            next[i] = {
                              ...tier,
                              max_years:
                                e.target.value === ''
                                  ? null
                                  : Number(e.target.value),
                            }
                            setEntitlementTiers(next)
                          }}
                          className='w-28 h-9 rounded-lg'
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type='number'
                          min={0}
                          step='0.5'
                          value={tier.days_per_month}
                          onChange={e => {
                            const next = [...entitlementTiers]
                            next[i] = {
                              ...tier,
                              days_per_month: Number(e.target.value),
                            }
                            setEntitlementTiers(next)
                          }}
                          className='w-28 h-9 rounded-lg'
                        />
                      </TableCell>
                      <TableCell className='text-right'>
                        <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary'>
                          {daysPerYear(tier.days_per_month)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button
                          type='button'
                          variant='ghost'
                          size='icon'
                          className='h-8 w-8 text-muted-foreground hover:text-destructive'
                          onClick={() =>
                            setEntitlementTiers(
                              entitlementTiers.filter((_, idx) => idx !== i)
                            )
                          }
                        >
                          <Trash2 className='w-4 h-4' />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div className='flex items-center justify-between max-w-lg pt-2 border-t border-border/50'>
            <div className='space-y-1'>
              <Label className='text-sm font-semibold'>
                Require probation confirmation
              </Label>
              <p className='text-xs text-muted-foreground'>
                Paid Leave only starts accruing the month after confirmation.
              </p>
            </div>
            <Switch
              checked={probationGateEnabled}
              onCheckedChange={setProbationGateEnabled}
            />
          </div>
        </div>
      </div>

      {/* Notice Period */}
      <div className='rounded-2xl border border-border overflow-hidden shrink-0'>
        <SectionHeader
          icon={BellRing}
          title='Notice Period'
          description='Working days notice required, by leave length'
          action={
            <Button
              type='button'
              size='sm'
              className='h-8 gap-1.5'
              onClick={() =>
                setNoticeTiers(prev => [
                  ...prev,
                  { max_days: null, notice_days: 0 },
                ])
              }
            >
              <Plus className='w-3.5 h-3.5' /> Add Tier
            </Button>
          }
        />
        <div className='px-6 py-6'>
          {noticeTiers.length === 0 ? (
            <p className='text-sm text-muted-foreground text-center py-6'>
              No notice tiers configured — leave requests won't be checked for
              notice period.
            </p>
          ) : (
            <div className='border border-border rounded-xl overflow-hidden'>
              <Table>
                <TableHeader>
                  <TableRow className='bg-muted/60'>
                    <TableHead>Leave length up to (days)</TableHead>
                    <TableHead>Notice required (days)</TableHead>
                    <TableHead className='w-10' />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {noticeTiers.map((tier, i) => (
                    <TableRow key={i} className='hover:bg-muted/40'>
                      <TableCell>
                        <Input
                          type='number'
                          min={0}
                          placeholder='Any length'
                          value={tier.max_days ?? ''}
                          onChange={e => {
                            const next = [...noticeTiers]
                            next[i] = {
                              ...tier,
                              max_days:
                                e.target.value === ''
                                  ? null
                                  : Number(e.target.value),
                            }
                            setNoticeTiers(next)
                          }}
                          className='w-32 h-9 rounded-lg'
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type='number'
                          min={0}
                          value={tier.notice_days}
                          onChange={e => {
                            const next = [...noticeTiers]
                            next[i] = {
                              ...tier,
                              notice_days: Number(e.target.value),
                            }
                            setNoticeTiers(next)
                          }}
                          className='w-32 h-9 rounded-lg'
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          type='button'
                          variant='ghost'
                          size='icon'
                          className='h-8 w-8 text-muted-foreground hover:text-destructive'
                          onClick={() =>
                            setNoticeTiers(
                              noticeTiers.filter((_, idx) => idx !== i)
                            )
                          }
                        >
                          <Trash2 className='w-4 h-4' />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* Weekly Off & Sandwich Rule */}
      <div className='rounded-2xl border border-border overflow-hidden shrink-0'>
        <SectionHeader
          icon={CalendarOff}
          title='Weekly Off & Sandwich Rule'
          description='Non-working days, and how gaps between leaves are handled'
        />
        <div className='px-6 py-6 space-y-6'>
          <div className='space-y-2'>
            <Label className='text-xs font-bold uppercase tracking-wider text-muted-foreground'>
              Weekly Off Days
            </Label>
            <div className='flex gap-2'>
              {WEEKDAY_LABELS.map((label, day) => (
                <button
                  key={day}
                  type='button'
                  onClick={() => toggleWeeklyOffDay(day)}
                  className={`w-12 h-10 rounded-xl text-xs font-semibold border transition-all ${
                    weeklyOffDays.includes(day)
                      ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                      : 'border-border text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className='flex items-center justify-between max-w-lg pt-2 border-t border-border/50'>
            <div className='space-y-1'>
              <Label className='text-sm font-semibold'>
                Sandwich-leave rule
              </Label>
              <p className='text-xs text-muted-foreground'>
                Count a weekly-off/holiday gap between two approved leaves as
                leave too.
              </p>
            </div>
            <Switch
              checked={sandwichRuleEnabled}
              onCheckedChange={setSandwichRuleEnabled}
            />
          </div>
        </div>
      </div>

      {/* Special Leave */}
      <div className='rounded-2xl border border-border overflow-hidden shrink-0'>
        <SectionHeader
          icon={Sparkles}
          title='Special Leave'
          description='Granted once per calendar year, never carried forward'
        />
        <div className='px-6 py-6'>
          <div className='space-y-2 max-w-xs'>
            <Label className='text-xs font-bold uppercase tracking-wider text-muted-foreground'>
              Days per year
            </Label>
            <Input
              type='number'
              min={0}
              step='0.5'
              value={specialLeaveDays}
              onChange={e => setSpecialLeaveDays(e.target.value)}
              className='h-10 rounded-lg'
            />
          </div>
        </div>
      </div>

      <div className='flex justify-end pt-2 shrink-0'>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className='rounded-lg px-6 shadow-lg shadow-primary/20 active:scale-95 transition-all'
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  )
}

export default LeavePolicy
